import { Node, mergeAttributes } from "@tiptap/core";

export const PersonaSummaryNode = Node.create({
  name: "personaSummary",
  group: "inline",
  inline: true,
  selectable: true,
  atom: false,

  addAttributes() {
    return {
      personaId: { default: null },
      summary: { default: "" },
      isEdited: { default: false },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="persona-summary"]',
        getAttrs: (element) => ({
          personaId: element.getAttribute("data-persona-id"),
          summary: element.getAttribute("data-summary") || element.textContent,
        }),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = mergeAttributes(HTMLAttributes, {
      "data-type": "persona-summary",
      "data-persona-id": node.attrs.personaId,
      "data-summary": node.attrs.summary,
      class: "persona-summary",
      contenteditable: "true",
    });

    return [
      "span",
      attrs,
      [
        "span",
        {
          class: "persona-summary-text",
        },
        node.attrs.summary,
      ],
    ];
  },

  addCommands() {
    return {
      setPersonaSummary:
        (attributes) =>
          ({ commands }) => {
            return commands.insertContent({
              type: this.name,
              attrs: attributes,
            });
          },
      updatePersonaSummary:
        (personaId, newSummary) =>
          ({ tr, state }) => {
            let updated = false;
            state.doc.descendants((node, pos) => {
              if (node.type.name === "personaSummary" && node.attrs.personaId === personaId) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  summary: newSummary,
                });
                updated = true;
              }
            });
            return updated;
          },
    };
  },

  addKeyboardShortcuts() {
    return {
      Backspace: ({ editor }) => {
        const { $from } = editor.state.selection;

        const showTooltip = () => {
          // Find the persona summary element
          const personaSummaryEl = editor.view.dom.querySelector('.persona-summary');
          if (personaSummaryEl) {
            // Create tooltip
            const tooltip = document.createElement('div');
            tooltip.className = 'persona-summary-tooltip';
            tooltip.textContent = "Persona Description can't be deleted.";
            document.body.appendChild(tooltip);

            // Position tooltip near the persona summary
            const rect = personaSummaryEl.getBoundingClientRect();
            tooltip.style.position = 'fixed';
            tooltip.style.top = `${rect.bottom + 8}px`;
            tooltip.style.left = `${rect.left}px`;
            tooltip.style.zIndex = '10000';

            // Remove tooltip after 3 seconds
            setTimeout(() => {
              if (tooltip.parentNode) {
                tooltip.parentNode.removeChild(tooltip);
              }
            }, 3000);
          }
        };

        // Check if we're at the start and the first node is persona summary
        if ($from.pos === 1) {
          const firstNode = editor.state.doc.firstChild;
          if (firstNode && firstNode.type.name === "personaSummary") {
            showTooltip();
            return true; // Prevent deletion
          }
        }

        // Check if the node before cursor is persona summary
        if ($from.nodeBefore && $from.nodeBefore.type.name === "personaSummary") {
          showTooltip();
          return true; // Prevent deletion
        }

        return false;
      },
      Delete: ({ editor }) => {
        const { $from } = editor.state.selection;
        const nodeAfter = $from.nodeAfter;

        if (nodeAfter && nodeAfter.type.name === "personaSummary") {
          // Find the persona summary element
          const personaSummaryEl = editor.view.dom.querySelector('.persona-summary');
          if (personaSummaryEl) {
            // Create tooltip
            const tooltip = document.createElement('div');
            tooltip.className = 'persona-summary-tooltip';
            tooltip.textContent = "Persona Description can't be deleted";
            document.body.appendChild(tooltip);

            // Position tooltip near the persona summary
            const rect = personaSummaryEl.getBoundingClientRect();
            tooltip.style.position = 'fixed';
            tooltip.style.top = `${rect.bottom + 8}px`;
            tooltip.style.left = `${rect.left}px`;
            tooltip.style.zIndex = '10000';

            // Remove tooltip after 3 seconds
            setTimeout(() => {
              if (tooltip.parentNode) {
                tooltip.parentNode.removeChild(tooltip);
              }
            }, 3000);
          }
          return true; // Prevent deletion
        }

        return false;
      },
    };
  },

  addNodeView() {
    return ({ node, HTMLAttributes, getPos, editor }) => {
      let currentNode = node;
      const view = editor.view;
      let isEditing = false;
      let inputEl = null;

      const dom = document.createElement("span");
      dom.className = `persona-summary ${node.attrs.isEdited ? "edited" : ""}`;
      dom.style.position = "relative";
      dom.style.display = "inline-flex";
      dom.style.alignItems = "center";
      dom.style.flexWrap = "nowrap";
      dom.contentEditable = "false";
      Object.entries(HTMLAttributes).forEach(([k, v]) =>
        dom.setAttribute(k, v)
      );

      const textSpan = document.createElement("span");
      textSpan.className = "persona-summary-text";
      textSpan.textContent = currentNode.attrs.summary;
      Object.assign(textSpan.style, {
        display: "inline-block",
        padding: "0.2em 0.4em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        boxSizing: "border-box",
        verticalAlign: "middle",
        flex: "0 1 auto",
      });
      dom.appendChild(textSpan);

      const editIcon = document.createElement("span");
      editIcon.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-left: 10px; cursor: pointer; display: inline !important;">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
        </svg>
      `;
      editIcon.style.display = "inline-flex";
      editIcon.style.alignItems = "center";
      editIcon.style.flex = "0 0 auto";
      editIcon.addEventListener("click", (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        if (isEditing) {
          inputEl.blur();
        } else {
          startEditing();
        }
      });
      dom.appendChild(editIcon);

      const saveAndExit = async (newSummary) => {
        const pos = getPos();
        if (typeof pos !== "number") return;

        // Store the old summary before updating
        const oldSummary = currentNode.attrs.summary;
        const personaId = currentNode.attrs.personaId;

        // Only mark as edited if the summary actually changed or if it was already edited
        const isSummaryChanged = newSummary !== oldSummary;
        const shouldBeEdited = isSummaryChanged || currentNode.attrs.isEdited;

        // Exit editing mode immediately (before API call)
        dom.classList.remove("editing");
        if (shouldBeEdited) {
          dom.classList.add("edited");
        }

        isEditing = false;
        if (inputEl && inputEl.parentNode)
          inputEl.parentNode.removeChild(inputEl);
        inputEl = null;
        textSpan.style.display = "inline-block";

        // Switch back to edit icon immediately
        editIcon.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-left: 10px; cursor: pointer; display: inline !important;">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
          </svg>
        `;

        // Update display with user input immediately
        textSpan.textContent = newSummary;

        // Call API if summary actually changed
        if (isSummaryChanged) {
          console.log('Updating persona summary:', { personaId, oldSummary, newSummary });

          try {
            const { updatePersonaSummaryAPI } = await import("../../../Services/userService.js");
            const PersonaStore = await import("../../../Store/PersonaStore.jsx");
            const { updatePersona } = PersonaStore.default.getState();

            const response = await updatePersonaSummaryAPI(personaId, newSummary);
            console.log('API response:', response);

            if (response.success && response.data && response.data.persona) {
              // Update persona in store
              updatePersona(response.data.persona);

              // Use the summary from API response (it might be different from user input)
              const apiSummary = response.data.persona.summary;

              // Update the node in the editor with API response
              const tr = view.state.tr.setNodeMarkup(pos, undefined, {
                ...currentNode.attrs,
                summary: apiSummary,
                isEdited: shouldBeEdited,
              });
              view.dispatch(tr);

              // Update the text span to show the API summary
              textSpan.textContent = apiSummary;

              // Update URL with the new summary
              const updatedPersona = response.data.persona;
              const { updateURLWithPersonaSummary, generatePersonaSummaryHTML } = await import("../../../Utils/personaSummaryUtils.js");
              const newSearchText = generatePersonaSummaryHTML(updatedPersona);
              await updateURLWithPersonaSummary(newSearchText);

              // Refresh gifts
              const SearchStore = await import("../../../Store/SearchStore.jsx");
              const { getWantedGifts } = SearchStore.default.getState();
              const Cookies = await import("js-cookie");
              const cookie = Cookies.default.get();
              await getWantedGifts(cookie, false);
            } else {
              console.error('Failed to update persona summary:', response);
              // Still update with user input if API fails
              const tr = view.state.tr.setNodeMarkup(pos, undefined, {
                ...currentNode.attrs,
                summary: newSummary,
                isEdited: shouldBeEdited,
              });
              view.dispatch(tr);
            }
          } catch (error) {
            console.error('Error updating persona summary:', error);
            // Still update with user input if error occurs
            const tr = view.state.tr.setNodeMarkup(pos, undefined, {
              ...currentNode.attrs,
              summary: newSummary,
              isEdited: shouldBeEdited,
            });
            view.dispatch(tr);
          }
        }
      };

      const startEditing = () => {
        if (isEditing) return;
        const pos = getPos();
        if (typeof pos !== "number") return;
        isEditing = true;

        // tag as editing
        dom.classList.add("editing");
        dom.classList.remove("edited");

        // Switch to checkmark icon
        editIcon.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-left: 4px; cursor: pointer; display: inline !important;">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
          </svg>
        `;

        // create input for single-line editing
        inputEl = document.createElement("input");
        inputEl.type = "text";
        inputEl.value = currentNode.attrs.summary;

        // copy styles
        const cs = window.getComputedStyle(textSpan);
        Object.assign(inputEl.style, {
          margin: 0,
          padding: cs.padding,
          font: cs.font,
          lineHeight: cs.lineHeight,
          letterSpacing: cs.letterSpacing,
          background: "transparent",
          border: "none",
          boxSizing: "border-box",
          outline: "none",
          whiteSpace: "nowrap",
          flex: "0 0 auto",
        });

        // Auto-size the input based on content
        const autoSize = () => {
          // Create a temporary span to measure text width
          const tempSpan = document.createElement("span");
          tempSpan.style.font = inputEl.style.font;
          tempSpan.style.padding = inputEl.style.padding;
          tempSpan.style.visibility = "hidden";
          tempSpan.style.position = "absolute";
          tempSpan.style.whiteSpace = "nowrap";
          tempSpan.textContent = inputEl.value || inputEl.placeholder || "";
          document.body.appendChild(tempSpan);
          const width = tempSpan.offsetWidth;
          document.body.removeChild(tempSpan);
          // Add a small buffer (20px) for cursor and set minimum width
          inputEl.style.width = Math.max(width - 20, 100) + "px";
        };

        // Replace textSpan with inputEl
        textSpan.style.display = "none";
        dom.insertBefore(inputEl, editIcon);

        autoSize();
        inputEl.focus();
        inputEl.select();

        // Update width on input
        inputEl.addEventListener("input", () => {
          autoSize();
        });

        // key handling
        inputEl.addEventListener("keydown", (evt) => {
          // Ctrl/Cmd+A
          const isSelectAll =
            evt.key.toLowerCase() === "a" && (evt.ctrlKey || evt.metaKey);
          if (isSelectAll) {
            evt.preventDefault();
            inputEl.select();
            return;
          }
          // commit
          if (evt.key === "Enter" || evt.key === "Tab") {
            evt.preventDefault();
            inputEl.blur();
          }
          // cancel
          else if (evt.key === "Escape") {
            evt.preventDefault();
            inputEl.value = currentNode.attrs.summary;
            inputEl.blur();
          }
        });

        // blur → save
        inputEl.addEventListener("blur", () => {
          if (inputEl) saveAndExit(inputEl.value);
        });
      };

      // 5) dblclick → edit
      dom.addEventListener("dblclick", (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        startEditing();
      });

      // Add touch event handlers for mobile
      let touchTimeout;
      let touchStartTime;

      dom.addEventListener("touchstart", (evt) => {
        touchStartTime = Date.now();
        touchTimeout = setTimeout(() => {
          evt.preventDefault();
          evt.stopPropagation();
          startEditing();
        }, 500); // 500ms for long press
      });

      dom.addEventListener("touchend", () => {
        clearTimeout(touchTimeout);
      });

      dom.addEventListener("touchmove", () => {
        clearTimeout(touchTimeout);
      });

      return {
        dom,
        stopEvent: () => isEditing,
        ignoreMutation: () => true,

        update(newNode) {
          if (newNode.type !== currentNode.type) return false;
          currentNode = newNode;

          Object.entries(currentNode.attrs).forEach(([k, v]) => {
            dom.setAttribute(`data-${k}`, String(v ?? ""));
          });
          textSpan.textContent = currentNode.attrs.summary;

          return true;
        },
      };
    };
  },
});
